import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../lib/api";
import jsPDF from "jspdf";
import { PageHeader } from "../components/PageHeader";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  Thermometer, SprayCan, Bug, Settings2, ChevronLeft, Plus, X, Check,
  CheckCircle2, Circle, CalendarDays, AlertTriangle, FileDown,
} from "lucide-react";

const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const WD = ["DOM", "LUN", "MAR", "MER", "GIO", "VEN", "SAB"];

const REGISTERS = {
  temperature: { title: "Registro di Controllo Temperature", short: "Refrigerazione", icon: Thermometer, desc: "Temperature di frigoriferi e celle, giorno per giorno" },
  cleaning: { title: "Registro di Pulizia e Sanificazione", short: "Sanificazione", icon: SprayCan, desc: "Flagga le pulizie dei macchinari per ogni giornata" },
  pest: { title: "Verifica e Difesa da Infestanti", short: "Infestanti", icon: Bug, desc: "Rilevazioni, presenze e provvedimenti adottati" },
};

const pad = (n) => String(n).padStart(2, "0");
const daysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();

export default function Haccp() {
  const now = new Date();
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "manager";
  const [backfilling, setBackfilling] = useState(false);
  const backfill = async () => {
    if (!window.confirm("Completare i registri HACCP dal 1° gennaio ad oggi?\n\nVerranno saltati i giorni di chiusura (domeniche fino al 31 maggio, Pasqua e Pasquetta). I giorni già compilati NON vengono modificati.")) return;
    setBackfilling(true);
    try {
      const { data } = await api.post("/haccp/backfill", { start: "2026-01-01", closed_dates: ["2026-04-06"], sundays_closed_until: "2026-05-31" });
      toast.success(`Registri completati: ${data.filled} giorni compilati · ${data.skipped_closed} giorni di chiusura saltati`);
      if (view !== "home") loadRecords(view);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBackfilling(false); }
  };
  const [view, setView] = useState("home"); // home | temperature | cleaning | pest
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth()); // 0-based
  const [config, setConfig] = useState({ fridges: [], fridge_types: {}, cleaning_items: [], cleaning_defaults: [], responsible: "" });
  const [records, setRecords] = useState({}); // date -> doc
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [business, setBusiness] = useState({ name: "Dogana 2.0", address: "", vat: "", phone: "" });
  const [include, setInclude] = useState({ temperature: true, cleaning: true, pest: true });

  const [compileOpen, setCompileOpen] = useState(false);
  const [activeDate, setActiveDate] = useState(null);
  const [draft, setDraft] = useState({});
  const [operator, setOperator] = useState("");

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [cfgDraft, setCfgDraft] = useState({ fridges: [], fridge_types: {}, cleaning_items: [], cleaning_defaults: [], responsible: "" });
  const [newItem, setNewItem] = useState("");

  const monthStr = `${year}-${pad(month + 1)}`;

  const loadConfig = async () => {
    try { const { data } = await api.get("/haccp/config"); setConfig(data); } catch { /* noop */ }
  };
  const loadRecords = async (register) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/haccp/daily?register=${register}&month=${monthStr}`);
      const map = {}; data.forEach((d) => { map[d.date] = d; });
      setRecords(map);
    } catch { setRecords({}); } finally { setLoading(false); }
  };

  useEffect(() => { loadConfig(); api.get("/settings/business").then(({ data }) => setBusiness(data)).catch(() => {}); }, []);
  useEffect(() => { if (view !== "home") loadRecords(view); /* eslint-disable-next-line */ }, [view, monthStr]);

  const days = useMemo(() => {
    const total = daysInMonth(year, month);
    return Array.from({ length: total }, (_, i) => {
      const d = i + 1;
      const dateStr = `${year}-${pad(month + 1)}-${pad(d)}`;
      const wd = WD[new Date(year, month, d).getDay()];
      return { d, dateStr, wd };
    });
  }, [year, month]);

  // ---- Compile ----
  const openCompile = (dateStr) => {
    setActiveDate(dateStr);
    const existing = records[dateStr];
    setOperator(existing?.operator || "");
    if (view === "temperature") {
      const temps = {}; config.fridges.forEach((f) => { temps[f] = existing?.temps?.[f] ?? ""; });
      setDraft({ temps, status: existing?.status || "conforme" });
    } else if (view === "cleaning") {
      const base = existing ? (existing.cleaned || []) : (config.cleaning_defaults || []);
      setDraft({ cleaned: [...base] });
    } else {
      setDraft({ mode: existing?.mode || "Ispezione visiva", presence: existing?.presence || "Nessuna", actions: existing?.actions || "" });
    }
    setCompileOpen(true);
  };

  const saveCompile = async () => {
    const payload = { register: view, date: activeDate, operator };
    if (view === "temperature") { payload.temps = draft.temps; payload.status = draft.status; }
    else if (view === "cleaning") { payload.cleaned = draft.cleaned; }
    else { payload.mode = draft.mode; payload.presence = draft.presence; payload.actions = draft.actions; }
    try {
      const { data } = await api.post("/haccp/daily", payload);
      setRecords((r) => ({ ...r, [activeDate]: data }));
      toast.success("Giornata registrata");
      setCompileOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleCleaned = (item) => setDraft((d) => {
    const set = new Set(d.cleaned); set.has(item) ? set.delete(item) : set.add(item);
    return { ...d, cleaned: [...set] };
  });

  // ---- Settings ----
  const openSettings = () => { setCfgDraft(JSON.parse(JSON.stringify(config))); setNewItem(""); setSettingsOpen(true); };
  const saveSettings = async () => {
    try { await api.put("/haccp/config", cfgDraft); setConfig(cfgDraft); toast.success("Impostazioni salvate"); setSettingsOpen(false); if (view !== "home") loadRecords(view); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const cfgListKey = view === "temperature" ? "fridges" : "cleaning_items";
  const addCfgItem = () => {
    const v = newItem.trim(); if (!v) return;
    setCfgDraft((c) => ({ ...c, [cfgListKey]: [...(c[cfgListKey] || []), v] })); setNewItem("");
  };
  const removeCfgItem = (i) => setCfgDraft((c) => {
    const list = [...c[cfgListKey]]; const removed = list.splice(i, 1)[0];
    const defaults = (c.cleaning_defaults || []).filter((x) => x !== removed);
    return { ...c, [cfgListKey]: list, cleaning_defaults: defaults };
  });
  const toggleDefault = (item) => setCfgDraft((c) => {
    const set = new Set(c.cleaning_defaults || []); set.has(item) ? set.delete(item) : set.add(item);
    return { ...c, cleaning_defaults: [...set] };
  });
  const setFridgeType = (name, type) => setCfgDraft((c) => ({ ...c, fridge_types: { ...(c.fridge_types || {}), [name]: type } }));

  const loadLogo = () => new Promise((resolve) => {
    fetch("/logo-dogana.png").then((r) => r.blob()).then((b) => {
      const fr = new FileReader(); fr.onload = () => resolve(fr.result); fr.onerror = () => resolve(null); fr.readAsDataURL(b);
    }).catch(() => resolve(null));
  });

  const doPrint = async () => {
    try { await api.put("/settings/business", business); } catch { /* noop */ }
    await generatePDF();
    setPrintOpen(false);
  };

  const generatePDF = async () => {
    setPdfLoading(true);
    try {
      const [cfgR, tR, cR, pR] = await Promise.all([
        api.get("/haccp/config"),
        api.get(`/haccp/daily?register=temperature&month=${monthStr}`),
        api.get(`/haccp/daily?register=cleaning&month=${monthStr}`),
        api.get(`/haccp/daily?register=pest&month=${monthStr}`),
      ]);
      const cfg = cfgR.data;
      const idx = (arr) => { const m = {}; arr.data.forEach((x) => { m[x.date] = x; }); return m; };
      const t = idx(tR), c = idx(cR), p = idx(pR);
      const doc = new jsPDF();
      const logo = await loadLogo();
      if (logo) { try { doc.addImage(logo, "PNG", 12, 9, 20, 20); } catch { /* noop */ } }
      const hx = logo ? 37 : 12;
      doc.setFontSize(14); doc.setFont(undefined, "bold"); doc.text(business.name || "Dogana 2.0", hx, 16);
      doc.setFontSize(9); doc.setFont(undefined, "normal");
      let hy = 21;
      if (business.address) { doc.text(business.address, hx, hy); hy += 4.5; }
      const l2 = [business.vat && `P.IVA: ${business.vat}`, business.phone && `Tel: ${business.phone}`].filter(Boolean).join("    ");
      if (l2) { doc.text(l2, hx, hy); }
      doc.setDrawColor(180); doc.line(12, 32, 198, 32);
      doc.setFontSize(13); doc.setFont(undefined, "bold"); doc.text(`Registro HACCP — ${MONTHS[month]} ${year}`, 12, 40);
      let y = 48;
      const line = (txt, size = 9, bold = false) => {
        doc.setFontSize(size); doc.setFont(undefined, bold ? "bold" : "normal");
        doc.splitTextToSize(txt, 186).forEach((pt) => { if (y > 285) { doc.addPage(); y = 15; } doc.text(pt, 12, y); y += size * 0.5 + 2; });
      };
      if (include.temperature) {
        line("1) REGISTRO CONTROLLO TEMPERATURE", 12, true);
        line(`Apparecchi: ${cfg.fridges.join(", ") || "—"}`, 8);
        days.forEach(({ d, dateStr, wd }) => {
          const r = t[dateStr];
          const vals = r ? cfg.fridges.map((f) => `${f}:${r.temps?.[f] ? r.temps[f] + "°C" : "—"}`).join("  ") : "non compilato";
          line(`Giorno ${d} (${wd})  ${vals}${r ? `  [${r.status || ""}]  Firma: ${r.operator || ""}` : ""}`, 8);
        });
        y += 4;
      }
      if (include.cleaning) {
        line("2) REGISTRO PULIZIA E SANIFICAZIONE", 12, true);
        days.forEach(({ d, dateStr, wd }) => {
          const r = c[dateStr];
          const txt = r ? `${(r.cleaned || []).length}/${cfg.cleaning_items.length} puliti — ${(r.cleaned || []).join(", ") || "nessuno"}` : "non compilato";
          line(`Giorno ${d} (${wd})  ${txt}${r ? `  Firma: ${r.operator || ""}` : ""}`, 8);
        });
        y += 4;
      }
      if (include.pest) {
        line("3) VERIFICA E DIFESA DA INFESTANTI", 12, true);
        days.forEach(({ d, dateStr, wd }) => {
          const r = p[dateStr];
          const txt = r ? `Rilevazione: ${r.mode || "—"} | Presenze: ${r.presence || "—"} | Provvedimenti: ${r.actions || "—"}` : "non compilato";
          line(`Giorno ${d} (${wd})  ${txt}${r ? `  Firma: ${r.operator || ""}` : ""}`, 8);
        });
      }
      doc.save(`registro-haccp-${monthStr}.pdf`);
      toast.success("Registro PDF generato");
    } catch (e) { toast.error("Impossibile generare il PDF"); }
    finally { setPdfLoading(false); }
  };

  // ---- Render: HOME ----
  if (view === "home") {
    return (
      <div>
        <PageHeader title="HACCP" subtitle="Registri di controllo e sicurezza alimentare" testid="haccp-header" />
        <div className="flex flex-wrap items-center gap-3 mb-6 p-4 rounded-2xl border border-border/70 bg-secondary/30">
          <span className="text-sm font-medium mr-1">Stampa registro mensile:</span>
          <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v))}>
            <SelectTrigger className="w-40 rounded-full bg-background" data-testid="pdf-month-select"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i)}>{m}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
            <SelectTrigger className="w-28 rounded-full bg-background" data-testid="pdf-year-select"><SelectValue /></SelectTrigger>
            <SelectContent>{[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={() => setPrintOpen(true)} className="rounded-full" data-testid="haccp-pdf-button">
            <FileDown className="w-4 h-4 mr-1" /> Stampa registro (ASL)
          </Button>
          {canManage && <Button onClick={backfill} disabled={backfilling} variant="outline" className="rounded-full" data-testid="haccp-backfill-button"><CalendarDays className="w-4 h-4 mr-1" />{backfilling ? "Completo…" : "Completa registri (da Gennaio)"}</Button>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {Object.entries(REGISTERS).map(([key, r]) => (
            <button key={key} onClick={() => setView(key)} data-testid={`haccp-open-${key}`}
              className="text-left group">
              <Card className="p-6 rounded-2xl h-full transition-all hover:shadow-lg hover:-translate-y-0.5 border-border/70">
                <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4 group-hover:scale-105 transition-transform">
                  <r.icon className="w-6 h-6" />
                </div>
                <h3 className="font-dash text-lg font-semibold">{r.short}</h3>
                <p className="text-sm text-muted-foreground mt-1">{r.desc}</p>
                <span className="inline-block mt-4 text-sm text-primary font-medium">Compila registro →</span>
              </Card>
            </button>
          ))}
        </div>

        <Dialog open={printOpen} onOpenChange={setPrintOpen}>
          <DialogContent data-testid="haccp-print-dialog" className="max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Stampa registro HACCP — {MONTHS[month]} {year}</DialogTitle>
              <DialogDescription>Imposta l'intestazione e scegli quali sezioni includere nel PDF.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm font-medium">Intestazione (dati del locale)</p>
              <div className="space-y-2"><Label>Nome locale</Label><Input value={business.name} onChange={(e) => setBusiness({ ...business, name: e.target.value })} data-testid="biz-name-input" /></div>
              <div className="space-y-2"><Label>Indirizzo</Label><Input value={business.address} onChange={(e) => setBusiness({ ...business, address: e.target.value })} data-testid="biz-address-input" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>P.IVA</Label><Input value={business.vat} onChange={(e) => setBusiness({ ...business, vat: e.target.value })} data-testid="biz-vat-input" /></div>
                <div className="space-y-2"><Label>Telefono</Label><Input value={business.phone} onChange={(e) => setBusiness({ ...business, phone: e.target.value })} data-testid="biz-phone-input" /></div>
              </div>
              <p className="text-sm font-medium pt-1">Sezioni da includere</p>
              <div className="space-y-1">
                {[["temperature", "Controllo Temperature"], ["cleaning", "Pulizia e Sanificazione"], ["pest", "Difesa da Infestanti"]].map(([k, lbl]) => (
                  <button key={k} type="button" onClick={() => setInclude((s) => ({ ...s, [k]: !s[k] }))} data-testid={`incl-${k}`}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg border text-left text-sm transition-colors ${include[k] ? "border-primary bg-primary/10" : "border-border hover:bg-secondary/60"}`}>
                    <span className={`w-4 h-4 rounded flex items-center justify-center shrink-0 ${include[k] ? "bg-primary text-primary-foreground" : "border border-muted-foreground/40"}`}>{include[k] && <Check className="w-3 h-3" />}</span>{lbl}
                  </button>
                ))}
              </div>
            </div>
            <DialogFooter><Button onClick={doPrint} disabled={pdfLoading} className="rounded-full" data-testid="print-generate-button"><FileDown className="w-4 h-4 mr-1" />{pdfLoading ? "Genero…" : "Genera PDF"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  const meta = REGISTERS[view];
  const todayStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  const missing = days.filter((x) => x.dateStr <= todayStr && !records[x.dateStr]).length;

  // ---- Render: REGISTER ----
  return (
    <div>
      <div className="flex items-center gap-3 mb-2">
        <Button variant="ghost" size="icon" onClick={() => setView("home")} data-testid="haccp-back" className="rounded-full"><ChevronLeft className="w-5 h-5" /></Button>
        <div className="flex items-center gap-2">
          <meta.icon className="w-5 h-5 text-primary" />
          <h1 className="font-dash text-2xl font-semibold" data-testid="register-title">{meta.title}</h1>
        </div>
        {view !== "pest" && (
          <Button variant="outline" size="icon" onClick={openSettings} data-testid="haccp-settings-button" className="ml-auto rounded-full"><Settings2 className="w-4 h-4" /></Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v))}>
          <SelectTrigger className="w-40 rounded-full" data-testid="month-select"><SelectValue /></SelectTrigger>
          <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i)}>{m}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
          <SelectTrigger className="w-32 rounded-full" data-testid="year-select"><SelectValue /></SelectTrigger>
          <SelectContent>{[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {missing > 0 && (
        <div className="mb-5 flex items-start gap-2 rounded-xl border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 px-4 py-3 text-sm" data-testid="haccp-reminder">
          <AlertTriangle className="w-4 h-4 mt-0.5 text-[hsl(var(--warning))] shrink-0" />
          <span><b>Promemoria:</b> {missing} {missing === 1 ? "giornata non ancora compilata" : "giornate non ancora compilate"} fino ad oggi in {MONTHS[month]}. Ricordati di registrarle.</span>
        </div>
      )}

      <div className="mb-5 flex items-start gap-2 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 text-sm" data-testid="haccp-auto-note">
        <Check className="w-4 h-4 mt-0.5 text-primary shrink-0" />
        <span><b>Compilazione automatica attiva:</b> temperature (frigo 2–5°C, congelatori −25/−19°C) e controllo infestanti ogni giorno alle <b>08:00</b>; pulizie e sanificazioni alle <b>13:00</b> e alle <b>22:00</b>. Puoi sempre modificare a mano ogni giornata.</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {days.map(({ d, dateStr, wd }) => {
          const rec = records[dateStr];
          const done = !!rec;
          return (
            <Card key={dateStr} className={`p-4 rounded-2xl border ${done ? "border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/5" : "border-border/70"}`} data-testid={`day-card-${dateStr}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CalendarDays className="w-4 h-4 text-muted-foreground" />
                  <span className="font-semibold">Giorno {d}</span>
                  <span className="text-xs text-muted-foreground uppercase">{wd}</span>
                </div>
                {done
                  ? <span className="inline-flex items-center gap-1 text-[hsl(var(--success))] text-xs font-medium"><CheckCircle2 className="w-4 h-4" /> Compilato</span>
                  : <span className="inline-flex items-center gap-1 text-muted-foreground text-xs"><Circle className="w-4 h-4" /> Da compilare</span>}
              </div>

              <div className="mt-3 text-sm text-muted-foreground min-h-[24px]">
                {view === "temperature" && (
                  <div className="flex flex-wrap gap-2">
                    {config.fridges.map((f) => (
                      <span key={f} className="px-2 py-0.5 rounded-full bg-secondary text-xs">
                        {f}: <b className="text-foreground">{rec?.temps?.[f] ? `${rec.temps[f]}°C` : "—"}</b>
                      </span>
                    ))}
                    {config.fridges.length === 0 && <span className="text-xs">Configura i frigoriferi con l'icona impostazioni ⚙</span>}
                  </div>
                )}
                {view === "cleaning" && (
                  <span className="text-xs">{done ? `${rec.cleaned?.length || 0} / ${config.cleaning_items.length} macchinari puliti` : `${config.cleaning_items.length} macchinari da flaggare`}</span>
                )}
                {view === "pest" && (
                  done ? <span className="text-xs">Rilevazione: {rec.mode || "—"} · Presenze: {rec.presence || "nessuna"}</span>
                       : <span className="text-xs">Modalità rilevazione · Presenze · Provvedimenti</span>
                )}
              </div>

              <div className="mt-3 flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{rec?.operator ? `Firma: ${rec.operator}` : "Firma OSA"}</span>
                <Button size="sm" onClick={() => openCompile(dateStr)} className="rounded-full" data-testid={`compile-${dateStr}`}>
                  {done ? "Modifica" : "Compila"}
                </Button>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Compile dialog */}
      <Dialog open={compileOpen} onOpenChange={setCompileOpen}>
        <DialogContent data-testid="compile-dialog" className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{meta.short} · Giorno {activeDate?.split("-")[2]}</DialogTitle></DialogHeader>

          {view === "temperature" && (
            <div className="space-y-3">
              {config.fridges.map((f) => (
                <div key={f} className="flex items-center gap-3">
                  <Label className="w-28 shrink-0">{f}{config.fridge_types?.[f] === "freezer" ? " (cong.)" : ""}</Label>
                  <Input type="number" step="0.1" value={draft.temps?.[f] ?? ""} placeholder="°C"
                    onChange={(e) => setDraft((d) => ({ ...d, temps: { ...d.temps, [f]: e.target.value } }))}
                    data-testid={`temp-input-${f}`} />
                  <span className="text-muted-foreground text-sm">°C</span>
                </div>
              ))}
              <div className="space-y-2 pt-2">
                <Label>Esito</Label>
                <Select value={draft.status} onValueChange={(v) => setDraft((d) => ({ ...d, status: v }))}>
                  <SelectTrigger data-testid="temp-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="conforme">Conforme</SelectItem>
                    <SelectItem value="non conforme">Non conforme</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {view === "cleaning" && (
            <div className="space-y-3">
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" className="rounded-full" data-testid="mark-all-clean" onClick={() => setDraft({ cleaned: [...config.cleaning_items] })}>
                  <Check className="w-4 h-4 mr-1" /> Segna tutte pulite
                </Button>
                <Button type="button" variant="ghost" size="sm" className="rounded-full" data-testid="clear-all-clean" onClick={() => setDraft({ cleaned: [] })}>Azzera</Button>
              </div>
              <div className="grid grid-cols-1 gap-1 max-h-[42vh] overflow-y-auto pr-1">
                {config.cleaning_items.map((item) => {
                  const on = draft.cleaned?.includes(item);
                  return (
                    <button key={item} type="button" onClick={() => toggleCleaned(item)} data-testid={`clean-item-${item}`} aria-pressed={on}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg border text-left transition-colors ${on ? "border-[hsl(var(--success))] bg-[hsl(var(--success))]/10" : "border-border hover:bg-secondary/60"}`}>
                      <span className={`w-5 h-5 rounded-md flex items-center justify-center shrink-0 ${on ? "bg-[hsl(var(--success))] text-white" : "border border-muted-foreground/40"}`}>{on && <Check className="w-3.5 h-3.5" />}</span>
                      <span className="text-sm">{item}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {view === "pest" && (
            <div className="space-y-3">
              <div className="space-y-2"><Label>Modalità rilevazione</Label><Input value={draft.mode} onChange={(e) => setDraft((d) => ({ ...d, mode: e.target.value }))} placeholder="es. Ispezione visiva, trappole" data-testid="pest-mode-input" /></div>
              <div className="space-y-2"><Label>Presenze rilevate</Label><Input value={draft.presence} onChange={(e) => setDraft((d) => ({ ...d, presence: e.target.value }))} placeholder="es. Nessuna" data-testid="pest-presence-input" /></div>
              <div className="space-y-2"><Label>Provvedimenti adottati</Label><Textarea value={draft.actions} onChange={(e) => setDraft((d) => ({ ...d, actions: e.target.value }))} data-testid="pest-actions-input" /></div>
            </div>
          )}

          <div className="space-y-2 pt-2">
            <Label>Firma OSA / Operatore</Label>
            <Input value={operator} onChange={(e) => setOperator(e.target.value)} placeholder="Nome operatore" data-testid="compile-operator-input" />
          </div>

          <DialogFooter><Button onClick={saveCompile} className="rounded-full" data-testid="save-compile-button">Salva giornata</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent data-testid="haccp-settings-dialog" className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{view === "temperature" ? "Frigoriferi / Congelatori / Celle" : "Macchinari da pulire"}</DialogTitle>
            <DialogDescription>Aggiungi, rinomina o rimuovi gli elementi del registro.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2 pb-2 border-b border-border">
              <Label>Responsabile / OSA (firma sui registri)</Label>
              <Input value={cfgDraft.responsible || ""} onChange={(e) => setCfgDraft((c) => ({ ...c, responsible: e.target.value }))} placeholder="Nome e cognome del responsabile" data-testid="cfg-responsible-input" />
              <p className="text-xs text-muted-foreground">Questo nome comparirà come firma dell'operatore sui registri.</p>
            </div>
            <div className="flex gap-2">
              <Input value={newItem} onChange={(e) => setNewItem(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCfgItem())}
                placeholder={view === "temperature" ? "Aggiungi frigo (es. Cella carni)" : "Aggiungi macchinario"} data-testid="cfg-new-input" />
              <Button type="button" onClick={addCfgItem} className="rounded-full" data-testid="cfg-add-button"><Plus className="w-4 h-4" /></Button>
            </div>
            <div className="space-y-1 max-h-[50vh] overflow-y-auto">
              {(cfgDraft[cfgListKey] || []).map((item, i) => (
                <div key={`${item}-${i}`} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border" data-testid={`cfg-row-${item}`}>
                  <span className="flex-1 text-sm">{item}</span>
                  {view === "temperature" && (
                    <div className="flex gap-1">
                      <button type="button" onClick={() => setFridgeType(item, "fridge")} data-testid={`cfg-type-fridge-${item}`}
                        className={`text-xs px-2 py-0.5 rounded-full border ${(cfgDraft.fridge_types?.[item] || "fridge") === "fridge" ? "bg-sky-500/15 text-sky-600 border-sky-500/40" : "text-muted-foreground border-border"}`}>Frigo</button>
                      <button type="button" onClick={() => setFridgeType(item, "freezer")} data-testid={`cfg-type-freezer-${item}`}
                        className={`text-xs px-2 py-0.5 rounded-full border ${cfgDraft.fridge_types?.[item] === "freezer" ? "bg-blue-600/15 text-blue-700 border-blue-600/40" : "text-muted-foreground border-border"}`}>Congelatore</button>
                    </div>
                  )}
                  {view === "cleaning" && (
                    <button type="button" onClick={() => toggleDefault(item)} title="Attiva di default" data-testid={`cfg-default-${item}`}
                      className={`text-xs px-2 py-0.5 rounded-full border ${cfgDraft.cleaning_defaults?.includes(item) ? "bg-primary text-primary-foreground border-primary" : "text-muted-foreground border-border"}`}>
                      di default
                    </button>
                  )}
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeCfgItem(i)} data-testid={`cfg-remove-${i}`}><X className="w-4 h-4 text-destructive" /></Button>
                </div>
              ))}
              {(cfgDraft[cfgListKey] || []).length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">Nessun elemento. Aggiungine uno sopra.</p>}
            </div>
            {view === "cleaning" && <p className="text-xs text-muted-foreground">Gli elementi "di default" verranno pre-selezionati quando compili una giornata, per flaggare la pulizia più velocemente.</p>}
          </div>
          <DialogFooter><Button onClick={saveSettings} className="rounded-full" data-testid="save-settings-button">Salva impostazioni</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
